<template>
  <FormCustomFields
    v-model:field-values="localFieldValues"
    :context-message="contextMessage"
    :loading="loading"
    :fields="fields"
    :field-value-fragments="props.fieldValueFragments"
    :errors="errors"
    :force-enable-inputs="props.forceEnableInputs"
    :disable-worker-input-fields="props.disableWorkerInputFields"
    :disable-recruiter-input-fields="props.disableRecruiterInputFields"
  />
</template>

<script setup lang="ts">
import { watch, computed } from 'vue'
import { CustomFieldTypeValueInput, TypeSupportingCustomFieldValues } from '@/gql/graphql'
import {
  FieldableFormCustomFieldFragment,
  FieldableFormCustomFieldValueFragment,
} from '@/components/Fieldable/graphql/fragments'
import useFieldableCustomFields from '@/components/Fieldable/composables/useFieldableCustomFields'
import { useFieldableFormStore } from '@/components/Fieldable/store/useFieldableFormStore'
import { FragmentType, useFragment } from '@/gql'
import FormCustomFields from '@/components/Fieldable/FormCustomFields.vue'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'

const emits = defineEmits(['update:field-values'])

interface Props {
  accountId: string
  // Extra provider accounts to query alongside accountId (e.g. the client
  // company on a supplier-mirror hire). Falls back to [accountId] when empty.
  accountIds?: string[]
  fieldableType: TypeSupportingCustomFieldValues
  fieldValues: CustomFieldTypeValueInput[]
  // Optional pre-existing field-value fragments. Required for FileUpload
  // fields to render the previously-saved filename + download link on form
  // load; ignored for every other field type.
  fieldValueFragments?: FragmentType<typeof FieldableFormCustomFieldValueFragment>[]
  fieldableId?: string
  /*
   * Controls whether errors should be displayed.
   * This gives control to the parent component.
   */
  shouldDisplayErrors?: boolean
  contextMessage?: string
  forceEnableInputs?: boolean
  disableWorkerInputFields?: boolean
  disableRecruiterInputFields?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  accountIds: () => [],
  fieldValueFragments: () => [],
  fieldableId: undefined,
  shouldDisplayErrors: false,
  forceEnableInputs: false,
  disableWorkerInputFields: false,
  disableRecruiterInputFields: false,
})

const accounts = props.accountIds.length ? props.accountIds : [props.accountId]

const { fields, loading } = useFieldableCustomFields(
  props.accountId,
  accounts,
  [props.fieldableType],
  PER_PAGE_MAXIMUM,
  true,
)

const fieldableFormStore = useFieldableFormStore()

const localFieldValues = computed<CustomFieldTypeValueInput[]>({
  get: () => props.fieldValues,
  set: (newFieldValues) => {
    emits('update:field-values', newFieldValues)
  },
})

const updateFieldsOnStore = (
  newFieldsForStore: FragmentType<typeof FieldableFormCustomFieldFragment>[],
) => {
  fieldableFormStore.updateFields(
    newFieldsForStore.map((field) => useFragment(FieldableFormCustomFieldFragment, field)),
  )
}

// Immediate, because the store is shared page-wide: another form may have loaded
// its own fields into it since this one last mounted, and on a remount the fields
// come straight from the Apollo cache, so a non-immediate watch would never fire.
watch(
  fields,
  (newFields) => {
    updateFieldsOnStore(newFields)
  },
  { immediate: true },
)

watch(
  () => props.fieldValues,
  (newFieldValues) => {
    fieldableFormStore.updateFieldValues(newFieldValues)
  },
  { immediate: true },
)

const errors = computed(() => (props.shouldDisplayErrors ? fieldableFormStore.errors : {}))
</script>
